import React, { useEffect, useRef, useState } from "react";
import { agoLine, DOT } from "@/lib/stages";
import { nowFor, viewer } from "@/lib/stageNow";
import { ukDayTime } from "@/lib/time";

/* The status ring inside each coloured stage box: a pie that fills as the case moves forward
   (In review → In progress → Offer out), a tick for good news, a cross or dash once it's closed.
   Only the inner mark of a live stage moves — a slow, soft fade. Never the word, never the note. */
const PIE = 2 * Math.PI * 2;
export const RING = {
  new: { c: "#0B1220" }, sent: { c: "#0B1220" },
  review: { c: "#0A3DB8", pie: 0.25 }, progress: { c: "#0F766E", pie: 0.5 }, offer: { c: "#0D9488", pie: 0.75 },
  docs: { c: "#8A5200", pie: 0.375 }, preparing: { c: "#0F766E", pie: 0.5 },
  accepted: { c: "#0A7D5A" }, funded: { c: "#0B6B4F" },
  info: { c: "#12B76A" }, reach: { c: "#0055FF" }, due: { c: "#0055FF" },
  callback: { c: "#0040C1" }, credit: { c: "#0F766E", pie: 0.5 }, valuation: { c: "#7A4800" },
  declined: { c: "#64748B" }, withdrawn: { c: "#64748B" },
};
export const ringColor = (kind) => (RING[kind] || RING.sent).c;

/* tone → the coloured box class (portal-sys.css / portal-extras.css) */
export const ST_CLASS = {
  ink: "st-new", amber: "st-sent", blue: "st-rev", teal: "st-prog", mint: "st-off", acc: "st-acc",
  green: "st-fund", red: "st-dec", quiet: "st-quiet", slate: "st-neutral", attn: "st-attn", info: "st-info",
  grow: "st-grow", reach: "st-reach",
};

export function StageRing({ kind = "sent", live, size = 14, color }) {
  const { pie } = RING[kind] || RING.sent;
  const c = color || ringColor(kind);
  const pulse = live ? "sr-pulse" : undefined;
  return (
    <svg className="sr" width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden="true" data-ring={kind}>
      {kind === "funded" ? (
        <>
          <circle cx="7" cy="7" r="7" fill={c} />
          <path d="M4.2 7.3l1.9 1.9 3.7-4.1" stroke="var(--sr-knock, #fff)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </>
      ) : (
        <>
          <circle cx="7" cy="7" r="6" stroke={c} strokeWidth="1.5" strokeDasharray={kind === "new" ? "2.2 2.2" : undefined} />
          {pie != null && (
            <circle className={pulse} cx="7" cy="7" r="2" stroke={c} strokeWidth="4"
                    strokeDasharray={`${PIE * pie} ${PIE}`} transform="rotate(-90 7 7)" />
          )}
          {kind === "accepted" && <path d="M4.4 7.2l1.8 1.8 3.4-3.8" stroke={c} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />}
          {(kind === "info" || kind === "due") && <circle className={pulse} cx="7" cy="7" r="2.6" fill={c} />}
          {kind === "reach" && (
            <path className={pulse} transform="translate(3.6 3.6) scale(.283)" fill={c}
                  d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1z" />
          )}
          {kind === "declined" && <path d="M4.9 4.9l4.2 4.2M9.1 4.9l-4.2 4.2" stroke={c} strokeWidth="1.5" strokeLinecap="round" />}
          {kind === "withdrawn" && <path d="M4.5 7h5" stroke={c} strokeWidth="1.5" strokeLinecap="round" />}
        </>
      )}
    </svg>
  );
}

/** The coloured stage box — the small square dot and the word, as the portal always had it. */
export function StageBox({ state, testId, style }) {
  return (
    <span className={`st ${ST_CLASS[state?.tone] || ST_CLASS.slate}`} style={style} data-testid={testId} data-stage={state?.key || state?.ring}>
      <i className="sq" />{state?.label}
    </span>
  );
}

/* ---------- the stage card: right now, who it's waiting on, the next step, the last update ---------- */
const CARD_W = 300;
const CARD_H = 210;
const canHover = () => window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;

/** The card itself — the same white panel as the stage menu, its left edge on the stage box's left edge.
 *  Only the Hub also sees who set the stage and how, in small print. */
export function StageCard({ state, pos, testId }) {
  const aud = viewer();
  const n = nowFor(state, aud);
  const m = state.moved;
  const id = (k) => (testId ? `${testId}-${k}` : undefined);
  const rows = [["Right now", n.right, "now"], ["Waiting on", n.waiting, "waiting"], ["Next step", n.next, "next"], ["Last update", n.last, "last"]];
  return (
    <span className="st-card" role="tooltip" style={{ position: "fixed", width: CARD_W, ...pos }} data-testid={testId}>
      <span className="st-card-h">
        <i style={{ width: 8, height: 8, borderRadius: 2.5, flex: "none", background: DOT[state.tone] || DOT.slate }} />
        <b>{state.label}</b>
        <em data-testid={id("ago")}>{m?.at ? agoLine(m.at) : ""}</em>
      </span>
      {rows.filter(([, v]) => v).map(([k, v, key]) => (
        <span className="st-card-r" key={key}><i>{k}</i><span data-testid={id(key)}>{v}</span></span>
      ))}
      {aud === "hub" && m?.at && (
        <span className="st-card-f" data-testid={id("who")}>Stage set by {m.who} · {m.how} · {ukDayTime(m.at, "—")}</span>
      )}
    </span>
  );
}

/** Hover (or, on a phone, tap) a stage to open its card. `tap={false}` leaves taps to the element underneath. */
export function StageHover({ state, testId, tap = true, children }) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const timer = useRef(null);
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - CARD_W - 8));
    setPos(window.innerHeight - r.bottom > CARD_H + 16 || r.top < CARD_H + 16
      ? { top: r.bottom + 8, left } : { bottom: window.innerHeight - r.top + 8, left });
  };
  const hide = () => { clearTimeout(timer.current); setPos(null); };

  useEffect(() => {
    if (!pos) return undefined;
    const away = (e) => { if (!ref.current?.contains(e.target)) hide(); };
    const esc = (e) => { if (e.key === "Escape") hide(); };
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    document.addEventListener("pointerdown", away);
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("scroll", hide, true); window.removeEventListener("resize", hide);
      document.removeEventListener("pointerdown", away); window.removeEventListener("keydown", esc);
    };
  }, [pos]);
  useEffect(() => () => clearTimeout(timer.current), []);

  if (!state?.moved?.at) return children;
  const id = testId ? `${testId}-card` : "stage-card";
  return (
    <span ref={ref} className="st-hov"
          onMouseEnter={() => { if (canHover()) { clearTimeout(timer.current); timer.current = setTimeout(show, 140); } }}
          onMouseLeave={() => { if (canHover()) hide(); }}
          onMouseDown={() => { if (!tap && canHover()) hide(); }}
          onClick={(e) => {
            if (!tap || canHover()) return;
            e.preventDefault(); e.stopPropagation();
            if (pos) hide(); else show();
          }}
          data-testid={testId ? `${testId}-hover` : undefined}>
      {children}
      {pos && <StageCard state={state} pos={pos} testId={id} />}
    </span>
  );
}

/** A stage: the coloured box on its own. Hover it (or tap it on a phone) for who moved it. */
export function StagePill({ state, testId, style }) {
  if (!state) return null;
  return <StageHover state={state} testId={testId}><StageBox state={state} testId={testId} style={style} /></StageHover>;
}

export default StagePill;
